import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { integrationsService, getErrorMessage } from '@/services';
import { renewalRefusalOf } from '@/services/policies';
import {
  brokerLookupPlansService,
  lookupVendorsService,
  ownLookupPlanService,
} from '@/services/integrations';
import type { BrokerLookupPlanWrite, LookupVendorWrite } from '@/services/integrations';
import type {
  AdminAPILogsParams,
  InsurerCredentialsResponse,
  IntegrationConfig,
  IntegrationEnv,
} from '@/services/integrations';
import type {
  QuoteRequestPayload,
  QuoteSubmitPayload,
  ProposalPayload,
  PaymentInitiation,
} from '@/types';
import { RENEWAL_CUSTOMER_MISMATCH } from '@/types';

export function useSubmitQuoteRequest() {
  return useMutation({
    mutationFn: (data: QuoteRequestPayload | QuoteSubmitPayload) =>
      integrationsService.submitQuoteRequest(data),
    onError: (error) => {
      // B9: a customer mismatch on a renewal link is the wizard's OWN "Different
      // customer" confirm prompt, not a failure -- the same rule
      // `useCreatePolicy` follows for the policy-create path. An error toast on
      // top of that prompt would read as the submit having failed when it is
      // simply waiting for the broker to confirm and resubmit.
      if (renewalRefusalOf(error)?.code === RENEWAL_CUSTOMER_MISMATCH) return;
      toast.error(getErrorMessage(error));
    },
  });
}

export function useCreateProposal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ProposalPayload) => integrationsService.createProposal(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      toast.success('Proposal submitted successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useInitiatePayment() {
  return useMutation({
    mutationFn: (data: PaymentInitiation) => integrationsService.initiatePayment(data),
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function usePolicyStatus(policyNumber: string) {
  return useQuery({
    queryKey: ['policy-status', policyNumber],
    queryFn: () => integrationsService.getPolicyStatus(policyNumber),
    enabled: !!policyNumber,
    refetchInterval: 30000, // Poll every 30s for status updates
  });
}

export function usePreviousInsurers() {
  return useQuery({
    queryKey: ['previous-insurers'],
    queryFn: () => integrationsService.getPreviousInsurers(),
    staleTime: 10 * 60 * 1000, // 10 minutes — rarely changes
  });
}

export function useWebhooks() {
  return useQuery({
    queryKey: ['broker-webhooks'],
    queryFn: () => integrationsService.getWebhooks(),
  });
}

export function useCreateWebhook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { event_type: string; callback_url: string; secret_key: string }) =>
      integrationsService.createWebhook(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['broker-webhooks'] });
      toast.success('Webhook created');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useUpdateWebhook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<{ event_type: string; callback_url: string; secret_key: string; is_active: boolean }> }) =>
      integrationsService.updateWebhook(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['broker-webhooks'] });
      toast.success('Webhook updated');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useDeleteWebhook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => integrationsService.deleteWebhook(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['broker-webhooks'] });
      toast.success('Webhook deleted');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useAPIAccess() {
  return useQuery({
    queryKey: ['broker-api-access'],
    queryFn: () => integrationsService.getAPIAccess(),
  });
}

// Batch quote hooks
export function useUploadBatchQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => integrationsService.uploadBatchQuote(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batch-history'] });
      toast.success('Batch uploaded successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useBatchStatus(batchId: string) {
  return useQuery({
    queryKey: ['batch-status', batchId],
    queryFn: () => integrationsService.getBatchStatus(batchId),
    enabled: !!batchId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'completed' || status === 'failed' ? false : 5000;
    },
  });
}

export function useBatchHistory(page: number = 1) {
  return useQuery({
    queryKey: ['batch-history', page],
    queryFn: () => integrationsService.listBatches(page),
  });
}

// Admin hooks (super_admin + country_admin)
export function useAdminApiLogs(params: AdminAPILogsParams = {}) {
  return useQuery({
    queryKey: ['admin-api-logs', params],
    queryFn: () => integrationsService.adminListApiLogs(params),
    placeholderData: (previous) => previous,
  });
}

export function useAdminCircuitBreakers() {
  return useQuery({
    queryKey: ['admin-circuit-breakers'],
    queryFn: () => integrationsService.adminListCircuitBreakers(),
    refetchInterval: 15000, // poll state every 15s
  });
}

export function useAdminResetCircuitBreaker() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (insurerCode: string) =>
      integrationsService.adminResetCircuitBreaker(insurerCode),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-circuit-breakers'] });
      toast.success(data.message || 'Circuit breaker reset');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useAdminTestConnection() {
  return useMutation({
    mutationFn: ({
      insurerCode,
      environment,
    }: {
      insurerCode: string;
      environment: 'sandbox' | 'production';
    }) => integrationsService.adminTestConnection(insurerCode, environment),
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

// ---------------------------------------------------------------------------
// Broker ↔ insurer credentials
// ---------------------------------------------------------------------------

export function useInsurerCredentials() {
  return useQuery({
    queryKey: ['broker-insurer-credentials'],
    queryFn: () => integrationsService.getInsurerCredentials(),
  });
}

export function useRequestActivation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ insurerId, environment }: { insurerId: string; environment: IntegrationEnv }) =>
      integrationsService.requestActivation(insurerId, environment),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['broker-insurer-credentials'] });
      toast.success('Activation requested. Your administrator will be notified.');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

export function useActivationRequests() {
  return useQuery({
    queryKey: ['admin-activation-requests'],
    queryFn: () => integrationsService.listActivationRequests(),
  });
}

export function useUpdateInsurerCredentials() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      insurerId,
      environment,
      data,
    }: {
      insurerId: string;
      environment: IntegrationEnv;
      data: { fields: Record<string, string>; is_enabled?: boolean };
    }) => integrationsService.updateCredentials(insurerId, environment, data),
    onSuccess: (saved) => {
      // Put what the server says it now holds straight onto the card, then
      // refetch so the list agrees with the database rather than with us.
      // The response carries the whole per-environment block, so merging it
      // over the cached one leaves nothing stale (activation_requested, which
      // only the GET computes, is kept from the cache).
      const { insurer_id: insurerId, insurer_code: _code, environment, ...block } = saved;
      queryClient.setQueryData<InsurerCredentialsResponse>(
        ['broker-insurer-credentials'],
        (current) =>
          current && {
            ...current,
            results: current.results.map((row) =>
              row.insurer_id === insurerId
                ? { ...row, [environment]: { ...row[environment], ...block } }
                : row,
            ),
          },
      );
      queryClient.invalidateQueries({ queryKey: ['broker-insurer-credentials'] });
      toast.success('Credentials saved');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useDeleteInsurerCredentials() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ insurerId, environment }: { insurerId: string; environment: IntegrationEnv }) =>
      integrationsService.deleteCredentials(insurerId, environment),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['broker-insurer-credentials'] });
      toast.success('Credentials removed');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useTestInsurerConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ insurerId, environment }: { insurerId: string; environment: IntegrationEnv }) =>
      integrationsService.testConnection(insurerId, environment),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['broker-insurer-credentials'] });
      // The backend reports a reachable-but-unverified result as success with
      // an explanatory message; surface its wording rather than inventing one.
      if (result.success) {
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

// ---------------------------------------------------------------------------
// Country admin: integration configs
// ---------------------------------------------------------------------------

export function useAdminIntegrationConfigs(environment?: IntegrationEnv) {
  return useQuery({
    queryKey: ['admin-integration-configs', environment ?? 'all'],
    queryFn: () => integrationsService.adminListIntegrationConfigs(environment),
  });
}

export function useAdminCreateIntegrationConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      insurer: string;
      environment: IntegrationEnv;
      active_api_version: string;
      base_url?: string;
      service_endpoints?: Record<string, string>;
      is_enabled: boolean;
    }) => integrationsService.adminCreateIntegrationConfig(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-integration-configs'] });
      toast.success('Integration created');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useAdminUpdateIntegrationConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<Pick<IntegrationConfig, 'active_api_version' | 'is_enabled' | 'base_url' | 'service_endpoints'>>;
    }) => integrationsService.adminUpdateIntegrationConfig(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-integration-configs'] });
      toast.success('Integration updated');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useAdminBrokerCredentialStatus(params: {
  broker?: string;
  insurer?: string;
  environment?: IntegrationEnv;
} = {}) {
  return useQuery({
    queryKey: ['admin-broker-credential-status', params],
    queryFn: () => integrationsService.adminListBrokerCredentialStatus(params),
  });
}

// ---------------------------------------------------------------------------
// Vehicle lookup vendors (country admin)
// ---------------------------------------------------------------------------

export function useLookupVendors() {
  return useQuery({
    queryKey: ['admin-lookup-vendors'],
    queryFn: () => lookupVendorsService.list(),
  });
}

export function useSaveLookupVendor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id?: string; data: LookupVendorWrite }) =>
      id ? lookupVendorsService.update(id, data) : lookupVendorsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-lookup-vendors'] });
      toast.success('Lookup vendor saved');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

// ---------------------------------------------------------------------------
// Broker lookup plans — what a registration search costs each broker
// ---------------------------------------------------------------------------

export function useBrokerLookupPlans() {
  return useQuery({
    queryKey: ['admin-lookup-plans'],
    queryFn: () => brokerLookupPlansService.list(),
  });
}

export function useSaveBrokerLookupPlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id?: string; data: BrokerLookupPlanWrite }) =>
      id ? brokerLookupPlansService.update(id, data) : brokerLookupPlansService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-lookup-plans'] });
      toast.success('Lookup plan saved');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/** The broker's own plan. Also what tells the motor wizard whether to offer
 *  the paid search button at all, rather than offering it and 402-ing. */
export function useOwnLookupPlan() {
  return useQuery({
    queryKey: ['own-lookup-plan'],
    queryFn: () => ownLookupPlanService.get(),
  });
}

export function useSaveOwnLookupPlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { public_lookup_enabled?: boolean; public_daily_cap?: number }) =>
      ownLookupPlanService.update(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['own-lookup-plan'] });
      toast.success('Public search settings saved');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useWebhookDeliveries(id: string | null) {
  return useQuery({
    queryKey: ['broker-webhook-deliveries', id],
    queryFn: () => integrationsService.getWebhookDeliveries(id as string),
    enabled: !!id,
  });
}

export function useTestWebhook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => integrationsService.testWebhook(id),
    onSuccess: (result, id) => {
      queryClient.invalidateQueries({ queryKey: ['broker-webhook-deliveries', id] });
      if (result.status === 'delivered') {
        toast.success(`Test event delivered (HTTP ${String(result.last_status_code)})`);
      } else {
        toast.error(`Test event failed: ${String(result.last_error || 'no answer from your server')}`);
      }
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useResendWebhookDelivery(webhookId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (deliveryId: string) => integrationsService.resendWebhookDelivery(deliveryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['broker-webhook-deliveries', webhookId] });
      toast.success('Event sent again');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}
